import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setUser } from '../store/reducers/user';
import axios from 'axios';
import { BASE_URL } from '../utils/BaseUrl';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Grid,
  IconButton
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';

// Importing the images
import mSecureLogo from "../assets/images/logo.png";
import medgeLogo from "../assets/images/medge_dark_bg.webp";

const NEW_FEATURES = [
  { icon: ShieldOutlinedIcon, title: 'Zero-Trust Security', desc: 'Every session verified with device posture checks and strong authentication.' },
  { icon: PolicyOutlinedIcon, title: 'Policy Enforcement', desc: 'Clipboard, downloads, uploads, printing and screen controls — all manageable.' },
  { icon: FingerprintOutlinedIcon, title: 'Windows Hello SSO', desc: 'Biometric and PIN-based sign-in for a seamless, password-free experience.' },
  { icon: BlockOutlinedIcon, title: 'Ad & Tracker Blocking', desc: 'Built-in adblock keeps browsing fast, private and distraction-free.' },
  { icon: FilterAltOutlinedIcon, title: 'URL Filtering', desc: 'Restrict browsing to approved domains and keep your workforce on track.' },
  { icon: BrandingWatermarkOutlinedIcon, title: 'Dynamic Watermarking', desc: 'Screen watermarks deter leaks and keep sensitive content traceable.' }
];

export default function LoginBoxLikeFlutter() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const methods = useForm();
  
  useEffect(() => {
    document.title = "mSecure Browser - Modern Secure Browsing Platform";
  }, []);
  
  const emailValue = methods.watch('email');
  const passwordValue = methods.watch('password');
  const isFormFilled = !!(emailValue && passwordValue);

  const onFormSubmitHandler = async (formValue) => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.post(`${BASE_URL}auth/login`, formValue);
      const ok = response?.data?.success || !!response?.data?.session_Id;
      if (ok) {
        const user = response.data.user || response.data.user;
        const appToken = response.data.token || response.data.session_Id || "";
        
        // Prevent login if not superadmin and no permissions
        if (!user.superadmin && (!user.permissions || user.permissions.length === 0)) {
          setErrorMessage("Access Denied: You have no permissions assigned to your role.");
          return;
        }

        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', appToken);
        localStorage.setItem('loginTime', new Date().toISOString());
        dispatch(setUser(user));

        // Smart Redirect based on permissions
        if (user.superadmin) {
          window.location.href = "/home";
        } else {
          const perms = user.permissions.join(" ");
          if (perms.includes('DashboardRead')) window.location.href = "/home";
          else if (perms.includes('GroupsRead')) window.location.href = "/groups";
          else if (perms.includes('PoliciesRead')) window.location.href = "/policies";
          else if (perms.includes('UrlsRead') || perms.includes('Whitelist UrlsRead')) window.location.href = "/urls";
          else if (perms.includes('Active SessionsRead')) window.location.href = "/active-sessions";
          else if (perms.includes('Browser UpdatesRead')) window.location.href = "/browser-updates";
          else if (perms.includes('DevicesRead')) window.location.href = "/devices";
          else if (perms.includes('QuestionnaireRead')) window.location.href = "/questionnaire";
          else if (perms.includes('RoleRead')) window.location.href = "/role";
          else if (perms.includes('User ManagementRead')) window.location.href = "/user-management";
          else window.location.href = "/home"; // fallback
        }
      } else {
        const msg = response.data.message || 'Something went wrong';
        setErrorMessage(msg);
      }
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.response?.data?.message || 'Something went wrong';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        fontFamily: 'Inter, Arial, sans-serif'
      }}
    >
      {/* ---------- left: form panel ---------- */}
      <Box
        sx={{
          flex: { xs: '1 1 auto', md: '0 0 40%' },
          bgcolor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 4, sm: 6 },
          position: 'relative'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Box sx={{ mb: 5, textAlign: 'center' }}>
            <Box
              component="img"
              src={mSecureLogo}
              alt="mAccess Logo"
              sx={{ height: 40, width: 'auto', objectFit: 'contain' }}
            />
          </Box>

          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onFormSubmitHandler)}>
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#8898AA', mb: 1, letterSpacing: '0.5px' }}>
                  USERNAME
                </Typography>
                <TextField
                  placeholder="admin"
                  fullWidth
                  {...methods.register('email')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 48,
                      bgcolor: '#F8FAFC',
                      borderRadius: '8px',
                      color: '#334155',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#CBD5E1' },
                      '&.Mui-focused fieldset': { 
                        borderColor: '#94A3B8', 
                        borderWidth: '1px' 
                      },
                    },
                    '& .MuiInputAdornment-root': { color: '#94A3B8' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #F8FAFC inset !important',
                      WebkitTextFillColor: '#334155 !important',
                      borderRadius: 'inherit'
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#8898AA', mb: 1, letterSpacing: '0.5px' }}>
                  PASSWORD
                </Typography>
                <TextField
                  placeholder="••••••••"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  {...methods.register('password')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 48,
                      bgcolor: '#F8FAFC',
                      borderRadius: '8px',
                      color: '#334155',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#CBD5E1' },
                      '&.Mui-focused fieldset': { 
                        borderColor: '#94A3B8', 
                        borderWidth: '1px' 
                      },
                    },
                    '& .MuiInputAdornment-root': { color: '#94A3B8' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #F8FAFC inset !important',
                      WebkitTextFillColor: '#334155 !important',
                      borderRadius: 'inherit'
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          sx={{ color: '#94A3B8' }}
                        >
                          {showPassword ? <VisibilityOutlinedIcon sx={{ fontSize: 20 }} /> : <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              {errorMessage && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: 14, color: '#EF4444' }}>
                    {errorMessage}
                  </Typography>
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ 
                  height: 48,
                  bgcolor: isFormFilled ? '#0E213E' : '#A0ABC0', 
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: 15,
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: isFormFilled ? '#0B1A30' : '#8F9BB0',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#E2E8F0',
                    color: '#94A3B8'
                  }
                }}
              >
                {loading ? 'Signing in…' : 'Sign In securely'}
              </Button>
            </form>
          </FormProvider>
        </Box>
      </Box>

      {/* ---------- right: feature panel ---------- */}
      <Box
        sx={{
          flex: { xs: '1 1 auto', md: '0 0 60%' },
          bgcolor: '#0E213E',
          p: { xs: 3, sm: 4, md: 5 },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <Box sx={{ maxWidth: '800px', mb: 2 }}>
          <Typography 
            component="h1"
            sx={{ 
              fontWeight: '700 !important', 
              fontSize: { xs: '28px !important', md: '40px !important' }, 
              color: '#FFFFFF !important',
              lineHeight: '1.2 !important',
              mb: 0.5
            }}
          >
            Enterprise Browsing
          </Typography>
          <Typography 
            component="h2"
            sx={{ 
              fontWeight: '700 !important', 
              fontSize: { xs: '28px !important', md: '40px !important' }, 
              color: '#3B82F6 !important',
              lineHeight: '1.2 !important',
              mb: 1.5
            }}
          >
            Redefined for Modern Teams
          </Typography>
          
          <Typography 
            sx={{ 
              fontSize: 14, 
              color: '#94A3B8',
              maxWidth: '500px',
              lineHeight: 1.4
            }}
          >
            Enterprise-grade secure browsing, built for modern workforces to safely access corporate resources without compromising speed or privacy.
          </Typography>
        </Box>

        <Grid container spacing={2.5} sx={{ mt: 'auto', mb: 'auto' }}>
          {NEW_FEATURES.map((feature, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box 
                  sx={{ 
                    minWidth: 40,
                    width: 40, 
                    height: 40, 
                    borderRadius: '10px', 
                    bgcolor: 'rgba(255, 255, 255, 0.05)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                  }}
                >
                  <feature.icon sx={{ color: '#3B82F6', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography 
                    sx={{ 
                      fontWeight: 700, 
                      fontSize: 14, 
                      color: '#FFFFFF', 
                      mb: 0.25 
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    sx={{ 
                      fontSize: 13, 
                      color: '#94A3B8',
                      lineHeight: 1.3
                    }}
                  >
                    {feature.desc}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 'auto', pt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 10, color: '#94A3B8', letterSpacing: '1px', fontWeight: 600 }}>
            POWERED BY
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={medgeLogo}
              alt="mEDGE Logo"
              sx={{
                height: 36,
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}